import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment,
  Paper, 
  Button, 
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { openLink } from '../services/telegram';
import { colors } from '../theme/themeConfig';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SbpIcon from '../assets/icons/SbpIcon';
import { API_URL } from '../config/env';
import { User } from '../types';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
// Import UserContext and PageHeader
import { useUser } from '../contexts/UserContext';
import PageHeader from '../components/PageHeader';

const MIN_PAYMENT = 10;
const PAGE_TITLE = 'Покупка рефбаллов';
const HINT_TEXT = '1₽ = 1 рефбалл';

const BuyBonusPage: React.FC = () => {
  // Use the UserContext instead of managing user state locally
  const { user, telegramUser, isLoading: userLoading } = useUser();
  
  // Improved loading state with timeout
  const [isLoading, setIsLoading] = useState(true);
  const [loadStartTime] = useState(Date.now());
  
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState<string>("150"); // Default to minimum order (50 rubles = 150 refpoints)
  const [price, setPrice] = useState<number>(10);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const iconSize = isMobile ? 20 : 24;
  const username = telegramUser?.username || user?.telegramUsername || 'username';
  
  // Conversion rate: 2 рефбалла per 1 ruble
  const refPointsPerRuble = 2;
  
  // Update price when amount changes
  useEffect(() => {
    // Calculate price in rubles based on the number of refpoints
    const refPoints = inputValue === "" ? 0 : parseInt(inputValue);
    const rubles = Math.ceil(refPoints / refPointsPerRuble);
    setPrice(rubles);
  }, [inputValue]);
  
  // User data is now loaded from the UserContext
  useEffect(() => {
    
    // Update loading state based on context loading state
    setIsLoading(userLoading);
    
    // Add a timeout to prevent infinite loading
    const timer = setTimeout(() => {
      if (isLoading && (Date.now() - loadStartTime) > 5000) {
        setIsLoading(false);
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [userLoading, isLoading, loadStartTime]);
  
  const handleBack = () => {
    navigate(-1);
  };
  
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Allow empty input or valid numbers
    if (value === "" || /^\d+$/.test(value)) {
      setInputValue(value);
      
      // Calculate rubles for validation
      if (value !== "") {
        const refPoints = parseInt(value);
        const rubles = Math.ceil(refPoints / refPointsPerRuble);
        
        // Validate minimum order (50 rubles = 150 refpoints)
        if (rubles < MIN_PAYMENT) {
          setError(`Минимальная сумма заказа: ${MIN_PAYMENT * refPointsPerRuble} рефбаллов (${MIN_PAYMENT} руб.)`);
        } else {
          setError(null);
        }
      }
    }
  };
  
  const handlePurchase = async (paymentMethod: 'card' | 'sbp') => {
    const amount = inputValue === "" ? 0 : parseInt(inputValue);
    
    if (amount <= 0) {
      setError('Пожалуйста, укажите количество бонусов');
      return;
    } else if (amount < 2) {
      setError(`Минимальная сумма заказа: ${MIN_PAYMENT} рублей`);
      return;
    }
    
    try {
      setProcessing(true);
      setError(null);
      
      // Use telegramUser from context instead of fetching it directly
      if (!telegramUser) {
        setError('Не удалось получить данные пользователя');
        return;
      }
      
      // Prepare data for API call
      const bonusData = {
        telegramId: telegramUser.id,
        amountBonus: amount,
        paymentMethod,
      };
      
      // Make API call to create bonus payment
      const response = await fetch(`${API_URL}/payments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bonusData),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.paymentUrl) {
        throw new Error('Ошибка при создании платежа.');
      }
      
      // Open payment URL
      openLink(data.paymentUrl);

      // Redirect to homepage after a short delay
      setTimeout(() => {
        navigate('/');
      }, 500);
      
    } catch (error) {
      console.error('Error creating payment:', error);
      setError('Ошибка при создании платежа');
    } finally {
      setProcessing(false);
    }
  };
  
  // Create action button for PageHeader
  const actionButton = (
    <IconButton
      onClick={handleBack}
      sx={{
        color: colors.text.primary,
      }}
    >
      <CloseOutlinedIcon />
    </IconButton>
  );

  // Show loading state if user data is still loading
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <PageHeader
          title={PAGE_TITLE}
          actionButton={actionButton}
        />
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Show processing state when making a payment
  if (processing) {
    return (
      <Box
        sx={{
          backgroundColor: colors.background.default,
          minHeight: '100vh',
          padding: theme.spacing(2),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <PageHeader
          title={PAGE_TITLE}
          actionButton={actionButton}
        />
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(2),
        backgroundColor: colors.background.default,
        minHeight: '100vh',
      }}
    >
      <PageHeader
        title={PAGE_TITLE}
        subtitle={HINT_TEXT}
        actionButton={actionButton}
      />
        <Paper
          sx={{
            backgroundColor: colors.background.paper,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(2),
            width: '100%',
          maxWidth: '500px',
            marginBottom: theme.spacing(2),
          }}
        >
        <Typography
          sx={{
            color: colors.text.primary,
            fontSize: theme.typography.pxToRem(16),
            marginBottom: theme.spacing(1),
            paddingLeft: theme.spacing(1),
          }}
        >
          Количество
        </Typography>

          <TextField
            fullWidth
            type="text"
            variant="outlined"
            value={inputValue}
            onChange={handleAmountChange}
            placeholder="Введите количество"
            InputProps={{
            endAdornment: <InputAdornment position="end">
              <Typography sx={{ color: colors.text.primary }}>
                рефбаллов
                  </Typography>
            </InputAdornment>,
              sx: {
              backgroundColor: '#2A2A2A',
              borderRadius: theme.shape.borderRadius,
                color: colors.text.primary,
                '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.main,
                  borderWidth: 2,
                },
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                },
              }
            }}
            inputProps={{
              style: { color: colors.text.primary }
            }}
          />
          
          {/* Error message displayed right after input field */}
          {error && (
            <Typography
              sx={{
                color: colors.error.main,
                fontSize: theme.typography.pxToRem(14),
                mt: theme.spacing(1),
              }}
            >
              {error}
            </Typography>
          )}
          
        </Paper>

        <Paper
          sx={{
            backgroundColor: colors.background.paper,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(2),
            width: '100%',
          maxWidth: '500px',
          marginBottom: theme.spacing(2),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
        <Typography sx={{ color: colors.text.primary }}>
            К оплате
          </Typography>
          <Typography 
            sx={{ 
            color: colors.text.primary,
              fontSize: theme.typography.pxToRem(24),
            fontWeight: 500,
            }}
          >
            {price} ₽
          </Typography>
        </Paper>
      
      {/* Fixed button container at the bottom */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          backgroundColor: 'rgba(19, 47, 62, 0.9)', // Slightly transparent background
          backdropFilter: 'blur(8px)',
          borderTop: `1px solid ${colors.divider}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          zIndex: 10,
        }}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={() => handlePurchase('sbp')}
          disabled={processing || inputValue === "" || parseInt(inputValue) <= 0 || price < MIN_PAYMENT}
          sx={{
            width: '100%',
            padding: '12px',
            borderRadius: '12px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
            backgroundColor: colors.primary.main,
          }}
          startIcon={<SbpIcon width={iconSize} height={iconSize} />}
        >
          Оплатить через СБП
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={() => handlePurchase('card')}
          disabled={processing || inputValue === "" || parseInt(inputValue) <= 0 || price < MIN_PAYMENT}
          sx={{
            width: '100%',
            padding: '12px',
            borderRadius: '12px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
            backgroundColor: colors.primary.main,
          }}
          startIcon={<CreditCardOutlinedIcon sx={{ fontSize: iconSize }} />}
        >
          Оплатить картой
        </Button>
      </Box>
    </Box>
  );
};

export default BuyBonusPage;
